/**
 * TierFeatureMap — shared grid used in both the Admin Pricing panel
 * and the public-facing "See full feature comparison" toggle on the
 * Pricing / PaywallDialog pages.
 *
 * Props
 * -----
 * compact   bool   — single-row summary badges instead of full grid
 * adminMode bool   — show cost-on-hover cells (admin only)
 */
import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, DollarSign, Check, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

const TIER_ORDER = ['free','promo','basic','pro','premier','studio'];
const TIER_LABELS = {
  free:    'Free',
  promo:   "Writer's Launch",
  basic:   'Starter',
  pro:     'Pro',
  premier: 'Premier',
  studio:  'Studio',
};
const TIER_COLOR = {
  free:    'bg-zinc-700/40 text-zinc-300',
  promo:   'bg-emerald-700/40 text-emerald-200',
  basic:   'bg-sky-700/40 text-sky-200',
  pro:     'bg-violet-700/40 text-violet-200',
  premier: 'bg-amber-700/40 text-amber-200',
  studio:  'bg-rose-700/40 text-rose-200',
};

function CapCell({ enabled, costCents, cadence, adminMode }) {
  const cell = (
    <div className={cn(
      'flex items-center justify-center w-full h-full py-1',
      enabled ? 'text-emerald-400' : 'text-zinc-600',
    )}>
      {enabled
        ? <Check className="w-3.5 h-3.5" />
        : <X    className="w-3 h-3" />}
    </div>
  );

  if (!adminMode || !enabled || costCents == null) return cell;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{cell}</TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {costCents > 0
            ? <><DollarSign className="inline w-3 h-3 mr-0.5" />{(costCents / 100).toFixed(2)} / {cadence}</>
            : 'No direct cost'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function TierFeatureMap({ adminMode = false, initiallyOpen = false }) {
  const [open, setOpen]       = useState(initiallyOpen);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    const url = adminMode
      ? '/api/admin/plans/feature-map'
      : '/api/plans/feature-map-public';
    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [open, adminMode, data]);

  const visibleTiers = data
    ? TIER_ORDER.filter(t => data.tiers.some(x => x.id === t))
    : TIER_ORDER;

  return (
    <div className="w-full">
      <button
        data-testid="tier-feature-map-toggle"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
      >
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {open ? 'Hide' : 'See full'} feature comparison
      </button>

      {open && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-700/50 bg-zinc-900/60">
          {loading && (
            <div className="py-10 text-center text-xs text-zinc-500">Loading feature map…</div>
          )}
          {!loading && data && (
            <table className="w-full text-xs min-w-[640px]">
              <thead>
                <tr className="border-b border-zinc-700/50">
                  <th className="text-left px-4 py-3 text-zinc-400 font-medium w-44">Feature</th>
                  {visibleTiers.map(t => {
                    const tier = data.tiers.find(x => x.id === t);
                    return (
                      <th key={t} className="text-center px-2 py-3">
                        <Badge className={cn('text-[10px] px-1.5 py-0.5', TIER_COLOR[t] || 'bg-zinc-700 text-zinc-200')}>
                          {TIER_LABELS[t] || t}
                        </Badge>
                        {tier?.price > 0 && (
                          <div className="text-zinc-500 text-[10px] mt-0.5">${tier.price}/mo</div>
                        )}
                        {tier?.price === 0 && t !== 'free' && (
                          <div className="text-emerald-500 text-[10px] mt-0.5">Free</div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Group: AI Writing */}
                {[
                  { group: 'AI Writing', keys: ['ai_write_chapters','ai_rewrite_chapter','bulk_draft_novel','editorial_suggestions_realtime','create_story_bible_from_synopsis'] },
                  { group: 'World-Building & Research', keys: ['world_builder_deduce','claude_chat','claude_coach','revision_review'] },
                  { group: 'Production', keys: ['audiobook_generation','audiobook_voice_cloning','adobe_pdf_export','canva_cover_gen','copyscape_scan','convert_to_script'] },
                  { group: 'Publishing & Business', keys: ['publish_to_bookstore','royalties_dashboard','isbn_manager','marketing_panel','heat_map'] },
                  { group: 'Human Services', keys: ['writer_coaching','monthly_editor_review'] },
                ].map(({ group, keys }) => (
                  <>
                    <tr key={`group-${group}`} className="bg-zinc-800/40">
                      <td colSpan={visibleTiers.length + 1} className="px-4 py-1.5 text-[10px] font-semibold tracking-widest text-zinc-500 uppercase">
                        {group}
                      </td>
                    </tr>
                    {keys.map(fkey => {
                      const feat = data.features.find(f => f.key === fkey);
                      if (!feat) return null;
                      return (
                        <tr key={fkey} className="border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors">
                          <td className="px-4 py-2 text-zinc-300 font-medium">
                            {feat.label}
                            {adminMode && feat.cogs && (
                              <span className="ml-1.5 text-[9px] text-amber-500/70 uppercase tracking-wide">COGS</span>
                            )}
                          </td>
                          {visibleTiers.map(t => {
                            const tier = data.tiers.find(x => x.id === t);
                            const enabled = tier?.caps?.[fkey] === true;
                            return (
                              <td key={t} className="text-center px-2 py-2">
                                <CapCell
                                  enabled={enabled}
                                  costCents={feat.cost_per_use_cents}
                                  cadence={feat.cadence}
                                  adminMode={adminMode}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
